import * as Sentry from '@sentry/browser';
import _ from 'lodash';
import moment from 'moment';

import { setLocale as mProdSetLocale } from '@miotech/mprod';

import 'moment/locale/zh-cn';
import 'moment/locale/zh-hk';
import { UserLanguage, I18nResource, I18nObject } from '../types';

const FALL_BACK_LOCALE = UserLanguage.EN_US;
const availableLocales = [
  UserLanguage.EN_US,
  UserLanguage.ZH_CN,
  UserLanguage.ZH_HK,
];

const resource: I18nResource = {};
let selectedLocale = FALL_BACK_LOCALE;

function W(
  path: string,
  object?: I18nObject | null,
  fallback: string = '',
): string {
  let result;

  let template = (resource[selectedLocale] || {})[path];
  if (typeof template === 'undefined') {
    template = (resource[FALL_BACK_LOCALE] || {})[path];
  }

  if (object) {
    // populate default object
    const templateRegex = /{([^}{]+?)}/g;
    const variableNames = [];
    let variableNameMatchResult;
    while (
      ((variableNameMatchResult = templateRegex.exec(template)),
      variableNameMatchResult)
    ) {
      variableNames.push(variableNameMatchResult[1]);
    }
    const defaultObject = {};
    variableNames.forEach(name => {
      _.set(defaultObject, name, '');
    });

    const compiled = _.template(template, {
      interpolate: /{([^}{]+?)}/g,
    });
    result = compiled(_.merge(defaultObject, object));
  } else {
    result = template;
  }

  if (typeof result === 'undefined') {
    result = fallback || path.split('.').slice(-1)[0];
  }

  return result;
}

function E(errorCode: string | number, object: I18nObject) {
  return W(`error.d.${errorCode}`, object);
}

function setLocale(locale: UserLanguage) {
  const { enableSentry } = window.__config__;
  if (enableSentry) {
    Sentry.configureScope(scope => {
      scope.setTag('page_locale', locale);
    });
  }
  if (resource[locale]) {
    selectedLocale = locale;
    moment.locale(locale);
    mProdSetLocale(locale);
  } else {
    selectedLocale = FALL_BACK_LOCALE;
    moment.locale(FALL_BACK_LOCALE);
    mProdSetLocale(FALL_BACK_LOCALE);
  }
}

function getLocale() {
  return selectedLocale;
}

function addResource(resourceToAdd: I18nResource) {
  _.each(resourceToAdd, (resourcePerLocale, locale) => {
    if (!(locale in resource)) {
      resource[locale as UserLanguage] = {};
    }
    _.each(resourcePerLocale, (value, key) => {
      const localeObject = resource[locale as UserLanguage];
      if (localeObject) {
        localeObject[key] = value;
      }
    });
  });
}

function getSystemLocale() {
  const systemLocale = _.get(
    window,
    'navigator.language',
    'en-US',
  );
  if (availableLocales.indexOf(systemLocale) !== -1) {
    return systemLocale;
  }
  if (systemLocale === 'zh-TW') {
    return 'zh-HK';
  }
  if (systemLocale.indexOf('zh') !== -1) {
    return 'zh-CN';
  }
  return 'en-US';
}

export { W, E, setLocale, getLocale, addResource, getSystemLocale };
